import { type Change, type ChangeSet } from 'prosemirror-changeset'
import { Node } from 'prosemirror-model'

import { constants } from '@showrunner/prose-schemas'

import type { Line } from '#util/pagination/helpers'

import type { ScriptPagination } from './pagination/paginationPlugin'

const { NodeTypeMap } = constants

const noDeletionChanges: readonly DeletionChange[] = []
const noOwnedDeletionChanges: readonly OwnedDeletionChange[] = []

export interface DeletionChange {
  readonly at: number
  readonly revision: string | null
}

export interface OwnedDeletionChange {
  readonly change: DeletionChange
  readonly line: Line
}

/**
 * An immutable view of the deleted content in a revision ChangeSet.
 */
export class DeletionSet {
  static readonly empty = new DeletionSet([])

  static from(changeSet: ChangeSet<string | null>): DeletionSet {
    const changes = changeSet.changes.filter(hasDeletedContent)
    return changes.length === 0 ? DeletionSet.empty : new DeletionSet(changes)
  }

  private constructor(
    private readonly changes: readonly Change<string | null>[],
  ) {}

  all(): readonly DeletionChange[] {
    return this.between(0, Infinity)
  }

  // Deletions have no extent in the current document, so their range query is
  // inclusive at both ends. Consumers that need a rendered location must
  // resolve the event to a surviving line instead of treating it as a span.
  between(from: number, to: number): readonly DeletionChange[] {
    let deletions: DeletionChange[] | undefined

    for (const change of this.changes) {
      if (change.fromB < from || change.fromB > to) continue

      for (const span of change.deleted) {
        if (!deletions) deletions = []
        deletions.push({
          at: change.fromB,
          revision: span.data,
        })
      }
    }

    return deletions ?? noDeletionChanges
  }

  ownedBy(
    doc: Node,
    pagination: ScriptPagination,
  ): readonly OwnedDeletionChange[] {
    if (this === DeletionSet.empty) return noOwnedDeletionChanges

    const linesByPage = indexLinesByScriptPage(doc, pagination)
    let ownedDeletions: OwnedDeletionChange[] | undefined

    for (const change of this.all()) {
      const pageStart = scriptPageStart(doc, change.at)
      if (pageStart === null) continue

      const lines = linesByPage.get(pageStart)
      if (!lines) continue

      const line = deletionOwnerLine(lines, change.at)
      if (line) {
        if (!ownedDeletions) ownedDeletions = []
        ownedDeletions.push({ change, line })
      }
    }

    return ownedDeletions ?? noOwnedDeletionChanges
  }
}

function hasDeletedContent(change: Change<string | null>): boolean {
  return change.deleted.length > 0
}

function indexLinesByScriptPage(
  doc: Node,
  pagination: ScriptPagination,
): ReadonlyMap<number, readonly Line[]> {
  const linesByPage = new Map<number, Line[]>()

  for (const page of pagination.pages) {
    for (const line of page.lines) {
      if (!Number.isFinite(line.from) || !Number.isFinite(line.to)) continue

      const pageStart = scriptPageStart(doc, line.from)
      if (pageStart === null) continue

      const lines = linesByPage.get(pageStart)
      if (lines) lines.push(line)
      else linesByPage.set(pageStart, [line])
    }
  }

  return linesByPage
}

function deletionOwnerLine(lines: readonly Line[], at: number): Line | null {
  // A deletion within surviving text belongs to that line. At a shared line
  // endpoint, pagination order gives the deletion to the preceding line.
  const containingLine = lines.find((line) => line.from <= at && at <= line.to)
  if (containingLine) return containingLine

  // A deleted block collapses to a gap between siblings. Prefer the preceding
  // sibling's final line; for the first block, use the following sibling's
  // first line. All rendered sheets in the main script page share this scope.
  const previousLines = lines.filter((line) => line.to < at)
  if (previousLines.length > 0) return previousLines.at(-1) ?? null

  return lines.find((line) => line.from > at) ?? null
}

function scriptPageStart(doc: Node, pos: number): number | null {
  const $pos = doc.resolve(pos)
  for (let depth = $pos.depth; depth >= 0; depth--) {
    if ($pos.node(depth).type.name === NodeTypeMap.PAGE) {
      return $pos.start(depth)
    }
  }
  return null
}
